import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Link2, Unlink, RefreshCw, ExternalLink, PenLine, Trash2, Plus, Download, ChevronDown, Send, Sparkles } from "lucide-react";
import { RichTextEditor } from "@/components/RichTextEditor";
import { STEPS, DIMENSIONS } from "@/data/assessmentForm";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");

const Panel = ({ title, count, children, testId }) => (
  <section data-testid={testId} className="border border-[#E5E7EB] bg-white p-6">
    <div className="flex items-baseline justify-between">
      <h2 className="font-head text-xl font-bold">{title}</h2>
      {count !== undefined && <span className="text-xs text-[#9CA3AF] uppercase tracking-[0.15em]">{count} total</span>}
    </div>
    <div className="mt-4">{children}</div>
  </section>
);

const AgreementsManager = ({ headers }) => {
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = () => axios.get(`${API}/admin/agreements`, { headers }).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async (a) => {
    setBusyId(a.id);
    try {
      const res = await axios.get(`${API}/admin/agreements/${a.id}/download`, { headers, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const el = document.createElement("a");
      el.href = url;
      el.download = `agreement-${a.id.split("-")[0]}.pdf`;
      document.body.appendChild(el);
      el.click();
      el.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the agreement."); } finally { setBusyId(null); }
  };

  return (
    <div className="lg:col-span-2">
      <Panel title="Agreements & acknowledgments" count={rows.length} testId="admin-agreements-panel">
        {rows.length === 0 && (
          <p className="text-sm text-[#9CA3AF]">No agreements yet. Checkout terms and intake acknowledgments appear here.</p>
        )}
        {rows.map((a) => (
          <div key={a.id} data-testid={`admin-agreement-${a.id}`}
            className="border-t border-[#E5E7EB] py-3 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="font-semibold text-sm flex items-center gap-2">
                {a.name || "Client"}
                <span className="text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full bg-[#EDF0F5] text-[#041C3E]">
                  {a.type_label || a.title}
                </span>
              </p>
              <p className="text-xs text-[#9CA3AF] mt-0.5 truncate">
                {a.title} · v{a.version} · {a.email || "no email"} · {fmt(a.accepted_at)}
                {a.signature ? ` · signed: ${a.signature}` : ""}
              </p>
            </div>
            <button
              data-testid={`admin-agreement-download-${a.id}`}
              onClick={() => download(a)}
              disabled={busyId === a.id}
              className="shrink-0 inline-flex items-center gap-2 border border-[#041C3E] px-4 py-2 text-sm font-semibold hover:bg-[#041C3E] hover:text-white transition-colors disabled:opacity-50"
            >
              <Download size={14} /> {busyId === a.id ? "…" : "PDF"}
            </button>
          </div>
        ))}
      </Panel>
    </div>
  );
};

const TaxCertManager = ({ headers }) => {
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = () => axios.get(`${API}/admin/tax-cert-requests`, { headers }).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const markIssued = async (r) => {
    try {
      await axios.patch(`${API}/admin/tax-cert-requests/${r.id}`, { status: r.status === "issued" ? "new" : "issued" }, { headers });
      toast.success(r.status === "issued" ? "Marked as pending" : "Marked as issued");
      load();
    } catch { toast.error("Could not update"); }
  };

  const pending = rows.filter((r) => r.status !== "issued").length;

  return (
    <div className="lg:col-span-2">
      <Panel title="80G certificate requests" count={rows.length} testId="admin-taxcert-panel">
        {rows.length === 0 && <p className="text-sm text-[#9CA3AF]">No 80G requests yet. Donor requests from the dashboard appear here.</p>}
        {rows.length > 0 && <p className="text-xs text-[#9CA3AF] mb-1">{pending} pending · {rows.length - pending} issued</p>}
        {rows.map((r) => (
          <div key={r.id} data-testid={`admin-taxcert-${r.id}`} className="border-t border-[#E5E7EB] py-3">
            <div className="flex items-start justify-between gap-4">
              <button onClick={() => setOpenId(openId === r.id ? null : r.id)} className="text-left flex-1">
                <p className="font-semibold text-sm flex items-center gap-1.5">
                  <ChevronDown size={14} className={`transition-transform ${openId === r.id ? "rotate-180" : ""}`} />
                  {r.full_name}
                  <span className={`ml-1 text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full ${r.status === "issued" ? "bg-green-100 text-green-700" : "bg-[#FBEFEA] text-[#D94833]"}`}>
                    {r.status === "issued" ? "issued" : "pending"}
                  </span>
                </p>
                <p className="text-xs text-[#9CA3AF] mt-0.5 ml-5">Aadhaar {(r.aadhaar || "").replace(/\d(?=\d{4})/g, "•")}{r.pan ? ` · PAN ${r.pan}` : ""} · {r.email} · {fmt(r.created_at)}</p>
              </button>
              <div className="flex items-center gap-2 shrink-0">
                <a href={`mailto:${r.email}?subject=Your 80G tax certificate`} className="p-2 text-[#3A4A63] hover:text-[#041C3E]" title="Email donor"><ExternalLink size={15} /></a>
                <button
                  data-testid={`admin-taxcert-issue-${r.id}`}
                  onClick={() => markIssued(r)}
                  className={`p-2 ${r.status === "issued" ? "text-green-600" : "text-[#3A4A63] hover:text-green-600"}`}
                  title={r.status === "issued" ? "Mark as pending" : "Mark as issued"}
                >
                  <CheckCircle2 size={16} />
                </button>
              </div>
            </div>
            {openId === r.id && (
              <div className="mt-2 ml-5 text-sm text-[#162030]" data-testid={`admin-taxcert-detail-${r.id}`}>
                <p className="text-xs text-[#9CA3AF]">Full name (as per ID): <span className="text-[#162030]">{r.full_name}</span></p>
                <p className="text-xs text-[#9CA3AF] mt-1">Aadhaar: <span className="text-[#162030] font-semibold">{r.aadhaar || "—"}</span></p>
                <p className="text-xs text-[#9CA3AF] mt-1">PAN: <span className="text-[#162030] font-semibold">{r.pan || "—"}</span></p>
                <p className="text-xs text-[#9CA3AF] mt-1">Postal address:</p>
                <p className="whitespace-pre-wrap leading-relaxed">{r.address}</p>
                {r.issued_at && <p className="text-xs text-green-700 mt-2">Issued {fmt(r.issued_at)}</p>}
              </div>
            )}
          </div>
        ))}
      </Panel>
    </div>
  );
};

const GoogleCard = ({ google, headers, reload }) => {
  const connect = async () => {
    try {
      const res = await axios.get(`${API}/oauth/google/login`, { headers });
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start Google connect");
    }
  };
  const disconnect = async () => {
    await axios.post(`${API}/admin/google/disconnect`, {}, { headers });
    toast.success("Google disconnected");
    reload();
  };
  const copyRedirect = async () => {
    try { await navigator.clipboard.writeText(google.redirect_uri || ""); toast.success("Redirect address copied"); }
    catch { toast.error("Could not copy. Select and copy it manually."); }
  };
  const statusLabel = google.connected ? "Connected" : google.needs_reconnect ? "Reconnect needed" : google.demo ? "Demo mode" : "Not connected";
  const statusCls = google.connected
    ? "bg-green-100 text-green-700"
    : google.needs_reconnect ? "bg-[#FEF3C7] text-[#92400E]" : google.demo ? "bg-[#FBEFEA] text-[#D94833]" : "bg-[#EDF0F5] text-[#041C3E]";
  return (
    <Panel title="Google Calendar" testId="admin-google-panel">
      <div className="flex items-center gap-2 text-sm">
        {google.connected ? <CheckCircle2 size={16} className="text-green-600" /> : <XCircle size={16} className="text-[#D94833]" />}
        <span data-testid="admin-google-status" className="font-semibold">
          {google.connected ? `Connected as ${google.email}` : google.needs_reconnect ? `Reconnect needed${google.email ? ` (${google.email})` : ""}` : google.demo ? "Not configured yet" : "Not connected"}
        </span>
        <span className={`ml-1 text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full ${statusCls}`}>{statusLabel}</span>
      </div>
      {google.needs_reconnect && (
        <div data-testid="admin-google-reconnect" className="mt-3 bg-[#FFFBEB] border border-[#FDE68A] p-4 text-xs text-[#92400E] leading-relaxed">
          Your saved Google connection can no longer be refreshed, so bookings currently show preview availability. This
          usually happens when the earlier sign in included permissions the app no longer uses. Click <strong>Reconnect Google</strong>
          below to sign in again and restore real calendar invites and Meet links.
        </div>
      )}
      <p className="text-xs text-[#3A4A63] mt-3 leading-relaxed">
        When connected, client bookings create real Google Calendar events with Meet links on your calendar,
        clients receive genuine calendar invites, reschedules and cancellations stay in sync, and your existing
        busy times block those slots. Session emails continue to be sent through the app's own email service.
      </p>
      <div className="mt-4 bg-[#F3F4F6] p-4 text-xs text-[#3A4A63] space-y-1" data-testid="admin-google-redirect">
        <p className="font-bold uppercase tracking-[0.15em]">Authorized redirect address (register this in Google)</p>
        <div className="flex items-center gap-2 mt-1">
          <code data-testid="admin-google-redirect-uri" className="font-mono break-all bg-white px-2 py-1 flex-1">{google.redirect_uri}</code>
          <button data-testid="admin-google-copy-redirect" onClick={copyRedirect}
                  className="shrink-0 border border-[#041C3E] px-3 py-1.5 font-semibold hover:bg-[#041C3E] hover:text-white transition-colors">Copy</button>
        </div>
      </div>
      {google.demo && (
        <div data-testid="admin-google-setup" className="mt-3 bg-white border border-[#E5E7EB] p-4 text-xs text-[#3A4A63] space-y-1">
          <p className="font-bold uppercase tracking-[0.15em] text-[#041C3E]">To turn this on</p>
          <p>1. In Google Cloud Console, enable the <em>Google Calendar API</em>.</p>
          <p>2. Create an OAuth Web client and register the redirect address above.</p>
          <p>3. Add the real Client ID and Secret to this environment's settings, then reload this page.</p>
          <p className="text-[#9CA3AF]">Connect is unavailable until real Google credentials are in place.</p>
        </div>
      )}
      <div className="flex gap-3 mt-5">
        {google.connected ? (
          <button data-testid="admin-google-disconnect" onClick={disconnect}
                  className="inline-flex items-center gap-2 border border-[#041C3E] px-5 py-3 text-sm font-semibold hover:bg-[#041C3E] hover:text-white transition-colors">
            <Unlink size={14} /> Disconnect
          </button>
        ) : (
          <>
            <button data-testid="admin-google-connect" onClick={connect} disabled={google.demo}
                    className="inline-flex items-center gap-2 bg-[#041C3E] text-white px-5 py-3 text-sm font-semibold hover:bg-[#D94833] transition-colors disabled:opacity-40">
              <Link2 size={14} /> {google.needs_reconnect ? "Reconnect Google" : "Connect Google"}
            </button>
            {google.needs_reconnect && (
              <button data-testid="admin-google-disconnect" onClick={disconnect}
                      className="inline-flex items-center gap-2 border border-[#041C3E] px-5 py-3 text-sm font-semibold hover:bg-[#041C3E] hover:text-white transition-colors">
                <Unlink size={14} /> Clear connection
              </button>
            )}
          </>
        )}
      </div>
    </Panel>
  );
};

const STEP_LABELS = {
  profile: "Section A", "persona-select": "Persona", student: "Student",
  junior: "Junior Pro", international: "Intl", discovery: "Discovery",
  interview: "Interview", aptitude: "Aptitude", reflection: "Reflection", acknowledgement: "Sign-off",
};
const StatCard = ({ label, value, sub, tone }) => (
  <div className={`border p-4 ${tone === "bad" ? "border-[#FCA5A5] bg-[#FEF2F2]" : tone === "good" ? "border-[#86EFAC] bg-[#F0FDF4]" : "border-[#E5E7EB] bg-white"}`}>
    <p className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] font-bold">{label}</p>
    <p className="font-head text-3xl font-black text-[#041C3E] mt-1">{value}</p>
    {sub && <p className="text-xs text-[#3A4A63] mt-0.5">{sub}</p>}
  </div>
);

const AnalyticsManager = ({ headers }) => {
  const [data, setData] = useState(null);
  const load = () => axios.get(`${API}/admin/analytics`, { headers }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return null;

  const checkoutData = [
    { name: "Initiated", value: data.checkout.initiated },
    { name: "Completed", value: data.checkout.success },
    { name: "Failed/Left", value: data.checkout.failed },
  ];
  const intakeStepData = (data.intake.steps || []).map((s) => ({
    name: STEP_LABELS[s.step] || s.step, Started: s.started, Completed: s.completed,
  }));
  const catData = (data.by_category || []).map((c) => ({ name: c.category, value: c.count }));

  return (
    <Panel title="Engagement & funnel analytics" testId="admin-analytics-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#3A4A63]">
          Live from {data.total_events} tracked events.{" "}
          {data.webhook_configured ? "Google Sheet sync is on." : "Google Sheet sync not configured yet."}
        </p>
        <button data-testid="analytics-refresh" onClick={load} className="text-xs font-semibold border border-[#041C3E] px-3 py-1.5 hover:bg-[#041C3E] hover:text-white transition-colors">Refresh</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
        <StatCard label="Total events" value={data.total_events} />
        <StatCard label="Checkout drop-off" value={`${data.checkout.dropoff_rate}%`} sub={`${data.checkout.initiated} started · ${data.checkout.success} paid`} tone={data.checkout.dropoff_rate >= 50 ? "bad" : "good"} />
        <StatCard label="Intake drop-off" value={`${data.intake.dropoff_rate}%`} sub={`${data.intake.opened} opened · ${data.intake.submitted} submitted`} tone={data.intake.dropoff_rate >= 50 ? "bad" : "good"} />
        <StatCard label="Form submissions" value={data.intake.submitted} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <div data-testid="analytics-checkout-chart">
          <p className="text-[10px] uppercase tracking-[0.15em] text-[#D94833] font-bold mb-2">Checkout funnel</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={checkoutData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <RTooltip /><Bar dataKey="value" fill="#D94833" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div data-testid="analytics-category-chart">
          <p className="text-[10px] uppercase tracking-[0.15em] text-[#D94833] font-bold mb-2">Events by category</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={catData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <RTooltip /><Bar dataKey="value" fill="#041C3E" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-6" data-testid="analytics-intake-chart">
        <p className="text-[10px] uppercase tracking-[0.15em] text-[#D94833] font-bold mb-2">Intake form step drop-off (started vs completed)</p>
        {intakeStepData.length ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={intakeStepData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <RTooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Started" fill="#041C3E" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Completed" fill="#D94833" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-[#9CA3AF]">No intake step data yet. It appears once visitors move through the assessment.</p>
        )}
      </div>
    </Panel>
  );
};

const DOW = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];
const TZ_PRESETS = ["America/Los_Angeles", "America/New_York", "Europe/London", "Asia/Kolkata", "Asia/Dubai", "Australia/Sydney"];
const hourLabel = (h) => {
  if (h === 24 || h === 0) return h === 0 ? "12 AM" : "12 AM (next day)";
  const d = new Date(); d.setHours(h, 0, 0, 0);
  return d.toLocaleTimeString(undefined, { hour: "numeric" });
};

const MeetingsManager = ({ headers }) => {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState("");
  const load = () => axios.get(`${API}/admin/meetings`, { headers })
    .then((r) => setData(r.data)).catch(() => setData({ meetings: [] }));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cancel = async (m) => {
    if (!window.confirm(`Cancel ${m.meeting_label} with ${m.name}? This frees the slot and emails them.`)) return;
    setBusyId(m.id);
    try {
      await axios.post(`${API}/admin/meetings/${m.id}/cancel`, {}, { headers });
      toast.success("Call canceled and slot freed.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel call"); }
    finally { setBusyId(""); }
  };

  if (!data) return null;
  const rows = data.meetings || [];
  const active = rows.filter((m) => m.status !== "cancelled");
  const badge = (t) => t === "business"
    ? "bg-[#EEF2FF] text-[#3730A3]"
    : "bg-[#FEF2F0] text-[#D94833]";
  return (
    <Panel title="Booked calls" count={active.length} testId="admin-meetings-panel">
      <p className="text-xs text-[#3A4A63] leading-relaxed mb-4">
        Every call booked from your public <code className="font-mono">/book</code> link, newest first.
        Times are in your timezone ({data.owner_timezone}).
      </p>
      {rows.length === 0 ? (
        <p className="text-sm text-[#9CA3AF] py-4" data-testid="admin-meetings-empty">No calls booked yet.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((m) => (
            <div key={m.id} data-testid={`admin-meeting-${m.id}`}
                 className={`border border-[#E5E7EB] p-4 ${m.status === "cancelled" ? "opacity-55" : ""}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`text-[10px] uppercase tracking-[0.12em] font-bold px-2 py-0.5 rounded-full ${badge(m.meeting_type)}`}>
                  {m.meeting_label}
                </span>
                <span className="font-semibold text-sm text-[#041C3E]" data-testid={`admin-meeting-name-${m.id}`}>{m.name}</span>
                {m.status === "cancelled" && <span className="text-[10px] uppercase tracking-[0.12em] font-bold text-[#9CA3AF]">Canceled</span>}
              </div>
              <p className="text-sm text-[#041C3E] font-semibold mt-2" data-testid={`admin-meeting-when-${m.id}`}>{m.when_owner}</p>
              <p className="text-xs text-[#3A4A63] mt-1">
                {m.email}{m.phone ? ` · ${m.phone}` : ""}
              </p>
              {m.notes && <p className="text-xs text-[#3A4A63] mt-1 italic">“{m.notes}”</p>}
              <div className="flex items-center gap-4 mt-3">
                {m.meet_link ? (
                  <a href={m.meet_link} target="_blank" rel="noreferrer" data-testid={`admin-meeting-meet-${m.id}`}
                     className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#041C3E] hover:text-[#D94833]">
                    <ExternalLink size={13} /> Join Meet
                  </a>
                ) : (
                  <span className="text-xs text-[#9CA3AF]">Invite sent by email</span>
                )}
                {m.status !== "cancelled" && (
                  <button data-testid={`admin-meeting-cancel-${m.id}`} onClick={() => cancel(m)} disabled={busyId === m.id}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#D94833] hover:text-[#C03F2A] disabled:opacity-50">
                    <Trash2 size={13} /> {busyId === m.id ? "Canceling…" : "Cancel call"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
};

const AvailabilityManager = ({ headers, scope = "default", title = "Booking availability", intro }) => {
  const [tz, setTz] = useState("");
  const [windows, setWindows] = useState([]);
  const [days, setDays] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const biz = scope === "business";
  const tid = (name) => (biz ? `biz-${name}` : name);

  useEffect(() => {
    axios.get(`${API}/admin/availability`, { headers, params: { scope } })
      .then((r) => { setTz(r.data.timezone); setWindows(r.data.windows || []); setDays(r.data.days || []); })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleDay = (d) => setDays((p) => (p.includes(d) ? p.filter((x) => x !== d) : [...p, d].sort((a, b) => a - b)));
  const setWin = (i, k, v) => setWindows((p) => p.map((w, j) => (j === i ? { ...w, [k]: parseInt(v, 10) } : w)));
  const addWin = () => setWindows((p) => [...p, { start: 9, end: 17 }]);
  const rmWin = (i) => setWindows((p) => p.filter((_, j) => j !== i));

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/availability`, { timezone: tz.trim(), windows, days }, { headers, params: { scope } });
      toast.success("Availability saved. New bookings use these hours right away.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save availability"); }
    finally { setBusy(false); }
  };

  if (!loaded) return null;
  const selCls = "border border-[#E5E7EB] px-2 py-1.5 text-sm bg-white";
  return (
    <Panel title={title} testId={tid("admin-availability-panel")}>
      <p className="text-xs text-[#3A4A63] leading-relaxed">
        {intro || (<>Set your open hours in <strong>your own timezone</strong>. Visitors always see these converted to their local time,
        and everything outside these windows is blocked automatically. Change this whenever you travel.</>)}
      </p>

      <div className="mt-4">
        <p className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] font-bold mb-1">Your timezone</p>
        <input data-testid={tid("availability-timezone-input")} value={tz} onChange={(e) => setTz(e.target.value)}
               placeholder="America/Los_Angeles" className={`${selCls} w-full max-w-xs font-mono`} />
        <div className="flex flex-wrap gap-2 mt-2">
          {TZ_PRESETS.map((p) => (
            <button key={p} data-testid={tid(`availability-tz-preset-${p}`)} onClick={() => setTz(p)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${tz === p ? "bg-[#041C3E] text-white border-[#041C3E]" : "border-[#E5E7EB] hover:border-[#041C3E]"}`}>{p}</button>
          ))}
        </div>
      </div>

      <div className="mt-5">
        <p className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] font-bold mb-1">{biz ? "Days you take business meetings" : "Days you take sessions"}</p>
        <div className="flex flex-wrap gap-2">
          {DOW.map(([label, d]) => (
            <button key={d} data-testid={tid(`availability-day-${d}`)} onClick={() => toggleDay(d)}
                    className={`text-sm px-3 py-1.5 border font-semibold transition-colors ${days.includes(d) ? "bg-[#D94833] text-white border-[#D94833]" : "border-[#E5E7EB] hover:border-[#041C3E]"}`}>{label}</button>
          ))}
        </div>
      </div>

      <div className="mt-5">
        <p className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] font-bold mb-1">Open hours (your time)</p>
        <div className="space-y-2" data-testid={tid("availability-windows")}>
          {windows.map((w, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={tid(`availability-window-${i}`)}>
              <span className="text-xs text-[#3A4A63]">From</span>
              <select data-testid={tid(`availability-window-${i}-start`)} value={w.start} onChange={(e) => setWin(i, "start", e.target.value)} className={selCls}>
                {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
              </select>
              <span className="text-xs text-[#3A4A63]">to</span>
              <select data-testid={tid(`availability-window-${i}-end`)} value={w.end} onChange={(e) => setWin(i, "end", e.target.value)} className={selCls}>
                {Array.from({ length: 24 }, (_, h) => <option key={h + 1} value={h + 1}>{hourLabel(h + 1)}</option>)}
              </select>
              <button data-testid={tid(`availability-window-${i}-remove`)} onClick={() => rmWin(i)} className="p-1.5 text-[#3A4A63] hover:text-[#D94833]" title="Remove"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        <button data-testid={tid("availability-add-window")} onClick={addWin} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#041C3E] hover:text-[#D94833]">
          <Plus size={13} /> Add another window
        </button>
      </div>

      <button data-testid={tid("availability-save")} onClick={save} disabled={busy}
              className="mt-5 inline-flex items-center gap-2 bg-[#041C3E] text-white px-5 py-3 text-sm font-semibold hover:bg-[#D94833] transition-colors disabled:opacity-50">
        {busy ? "Saving…" : "Save availability"}
      </button>
    </Panel>
  );
};

const AddTestimonial = ({ headers, reload }) => {
  const [f, setF] = useState({ name: "", role: "", quote: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (f.quote.trim().length < 20) { toast.error("Quote must be at least 20 characters."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/testimonials`, f, { headers });
      toast.success("Testimonial added and published");
      setF({ name: "", role: "", quote: "" });
      reload();
    } catch { toast.error("Could not add testimonial"); }
    finally { setBusy(false); }
  };
  const inp = "w-full border border-[#E5E7EB] focus:border-[#041C3E] outline-none px-3 py-2 text-sm";
  return (
    <div data-testid="admin-add-testimonial" className="bg-[#F7F5F2] p-4 mb-4">
      <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#041C3E] mb-3">Add a testimonial</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input data-testid="add-testimonial-name" placeholder="Name (leave blank for Anonymous)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inp} />
        <input data-testid="add-testimonial-role" placeholder="Role (optional)" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className={inp} />
      </div>
      <textarea data-testid="add-testimonial-quote" rows={3} placeholder="Testimonial (min 20 characters)" value={f.quote} onChange={(e) => setF({ ...f, quote: e.target.value })} className={`${inp} mt-2`} />
      <button data-testid="add-testimonial-submit" onClick={submit} disabled={busy}
        className="mt-3 bg-[#D94833] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#C03F2A] transition-colors disabled:opacity-60">
        {busy ? "Adding…" : "Add & publish"}
      </button>
    </div>
  );
};

const TestimonialRow = ({ t, headers, reload }) => {
  const act = async (method, path) => {
    await axios({ method, url: `${API}/admin/testimonials/${t.id}${path}`, headers });
    reload();
  };
  return (
    <div data-testid={`admin-testimonial-${t.id}`} className="border-t border-[#E5E7EB] py-4 text-sm">
      <p className="leading-relaxed">“{t.quote}”</p>
      <p className="text-xs text-[#9CA3AF] mt-2">{t.name}{t.role ? ` · ${t.role}` : ""} · {fmt(t.created_at)}</p>
      <div className="flex gap-3 mt-3">
        {!t.approved && (
          <button data-testid={`admin-approve-${t.id}`} onClick={() => act("post", "/approve")}
                  className="bg-[#041C3E] text-white px-4 py-2 text-xs font-semibold hover:bg-[#D94833] transition-colors">Approve & publish</button>
        )}
        {t.approved && <span className="text-xs text-green-700 font-semibold self-center">Published</span>}
        <button data-testid={`admin-delete-${t.id}`} onClick={() => act("delete", "")}
                className="border border-[#E5E7EB] px-4 py-2 text-xs font-semibold hover:border-[#D94833] hover:text-[#D94833] transition-colors">Delete</button>
      </div>
    </div>
  );
};

const LEAD_STATUS_STYLES = {
  new: "bg-[#041C3E] text-white border-[#041C3E]",
  contacted: "bg-[#B45309] text-white border-[#B45309]",
  converted: "bg-[#15803D] text-white border-[#15803D]",
};

const LeadRow = ({ lead, headers, reload }) => {
  const [busy, setBusy] = useState(false);
  const status = lead.status || "new";
  const setStatus = async (s) => {
    if (s === status || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/leads/${lead.id}/status`, { status: s }, { headers });
      toast.success(`Marked as ${s}`);
      reload();
    } catch {
      toast.error("Could not update status");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid={`admin-lead-${lead.id}`} className="border-t border-[#E5E7EB] py-4 text-sm">
      <div className="flex justify-between gap-4 items-start">
        <div>
          <p className="font-semibold">{lead.name}</p>
          <a href={`mailto:${lead.email}`} data-testid={`admin-lead-email-${lead.id}`} className="text-xs text-[#041C3E] underline underline-offset-2 hover:text-[#D94833]">{lead.email}</a>
          <p className="text-xs text-[#9CA3AF] mt-1">via {lead.source} · {fmt(lead.created_at)}</p>
        </div>
        <a href={`mailto:${lead.email}`} className="shrink-0 bg-[#041C3E] text-white px-4 py-2 text-xs font-semibold hover:bg-[#D94833] transition-colors">Email lead</a>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <span className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">Status</span>
        {["new", "contacted", "converted"].map((s) => (
          <button
            key={s}
            data-testid={`admin-lead-status-${lead.id}-${s}`}
            disabled={busy}
            onClick={() => setStatus(s)}
            className={`text-[10px] font-bold uppercase tracking-[0.1em] border px-2.5 py-1 transition-colors disabled:opacity-50 ${
              status === s ? LEAD_STATUS_STYLES[s] : "bg-white text-[#3A4A63] border-[#E5E7EB] hover:border-[#041C3E]"
            }`}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
};

const money = (paise) => `₹${((paise || 0) / 100).toLocaleString("en-IN")}`;

const PAY_STATUS_STYLES = {
  paid: "text-green-700",
  partially_refunded: "text-[#B45309]",
  refunded: "text-[#9CA3AF]",
};

const PaymentRow = ({ p, headers, reload }) => {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const refunded = p.refunded_amount || 0;
  const remaining = (p.amount || 0) - refunded;
  const canRefund = p.payment_id && remaining > 0 && p.status !== "refunded";
  const status = p.status || "paid";

  const submit = async (full) => {
    let body = {};
    if (!full) {
      const rupees = parseFloat(amount);
      if (!rupees || rupees <= 0 || rupees * 100 > remaining) {
        toast.error(`Enter an amount between 1 and ${remaining / 100}`);
        return;
      }
      body = { amount: Math.round(rupees * 100) };
    }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/payments/${p.order_id}/refund`, body, { headers });
      toast.success("Refund processed");
      setOpen(false); setAmount(""); reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Refund failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`admin-payment-${p.order_id}`} className="border-t border-[#E5E7EB] py-3 text-sm">
      <div className="flex justify-between gap-4">
        <div>
          <p className="font-semibold">{p.name}</p>
          <p className="text-xs text-[#9CA3AF]">{p.email}</p>
        </div>
        <div className="text-right">
          <p>{p.tier_name} <span className="text-[#9CA3AF]">· {money(p.amount)}</span></p>
          <p className="text-xs text-[#9CA3AF]">{fmt(p.paid_at)}</p>
          <p className={`text-[10px] font-bold uppercase tracking-[0.1em] ${PAY_STATUS_STYLES[status] || ""}`}>
            {status.replace("_", " ")}{refunded > 0 ? ` · ${money(refunded)} refunded` : ""}
          </p>
        </div>
      </div>
      {canRefund && (
        <div className="mt-2">
          {!open ? (
            <button data-testid={`admin-refund-open-${p.order_id}`} onClick={() => setOpen(true)}
                    className="text-xs font-semibold text-[#D94833] underline underline-offset-2 hover:text-[#041C3E]">
              Issue refund
            </button>
          ) : (
            <div data-testid={`admin-refund-panel-${p.order_id}`} className="bg-[#F3F4F6] p-3 mt-1 space-y-2">
              <p className="text-xs text-[#3A4A63]">Refundable now: <strong>{money(remaining)}</strong></p>
              <div className="flex flex-wrap items-center gap-2">
                <button data-testid={`admin-refund-full-${p.order_id}`} disabled={busy} onClick={() => submit(true)}
                        className="bg-[#041C3E] text-white px-3 py-2 text-xs font-semibold hover:bg-[#D94833] transition-colors disabled:opacity-40">
                  Refund full {money(remaining)}
                </button>
                <span className="text-xs text-[#9CA3AF]">or</span>
                <input data-testid={`admin-refund-amount-${p.order_id}`} type="number" min="1" placeholder="₹ amount"
                       value={amount} onChange={(e) => setAmount(e.target.value)}
                       className="w-28 border border-[#E5E7EB] focus:border-[#041C3E] outline-none px-2 py-2 text-xs" />
                <button data-testid={`admin-refund-partial-${p.order_id}`} disabled={busy} onClick={() => submit(false)}
                        className="border border-[#041C3E] px-3 py-2 text-xs font-semibold hover:bg-[#041C3E] hover:text-white transition-colors disabled:opacity-40">
                  Refund amount
                </button>
                <button data-testid={`admin-refund-cancel-${p.order_id}`} onClick={() => { setOpen(false); setAmount(""); }}
                        className="text-xs text-[#9CA3AF] hover:text-[#041C3E]">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const FIELD_DEFS = STEPS.flatMap((s) => s.fields || []);

const csvCell = (v) => {
  let s = Array.isArray(v) ? v.join("; ") : v == null ? "" : String(v);
  if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
  return `"${s.replace(/"/g, '""')}"`;
};

const ReportBlock = ({ label, text }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.1em] text-[#9CA3AF] font-semibold mb-0.5">{label}</p>
    <p className="text-sm text-[#162030] leading-relaxed">{text}</p>
  </div>
);

const CollegeList = ({ label, items }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.1em] text-[#9CA3AF] font-semibold mb-1">{label}</p>
    <ol className="space-y-1.5">
      {(items || []).map((c, i) => (
        <li key={i} className="text-sm text-[#162030] flex gap-2">
          <span className="font-bold text-[#D94833]">{i + 1}</span>
          <span>
            <strong>{c.name}</strong>
            {c.location ? <span className="text-[#9CA3AF]"> · {c.location}</span> : null}
            {c.program ? <span className="block text-xs text-[#3A4A63]">{c.program}</span> : null}
          </span>
        </li>
      ))}
    </ol>
  </div>
);

const AssessmentsManager = ({ headers }) => {
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => axios.get(`${API}/admin/assessments`, { headers }).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (a) => {
    if (!window.confirm(`Delete ${a.name}'s assessment? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/assessments/${a.intake_token}`, { headers });
      toast.success("Assessment deleted");
      load();
    } catch { toast.error("Could not delete"); }
  };

  const resend = async (a, regenerate) => {
    const verb = regenerate ? "Regenerate the report and resend" : "Resend the report";
    if (!window.confirm(`${verb} to ${a.email || "this client"}?`)) return;
    setBusyId(a.intake_token);
    try {
      const r = await axios.post(`${API}/admin/assessments/${a.intake_token}/resend?regenerate=${regenerate ? "true" : "false"}`, {}, { headers });
      if (r.data.email_sent) toast.success(`Report sent to ${r.data.sent_to}`);
      else toast.warning("Report prepared, but no client email was on file");
      load();
    } catch { toast.error("Could not resend the report"); }
    finally { setBusyId(null); }
  };

  const personasOf = (a) => a.profile?.personas || (a.persona ? [a.persona] : []);

  const downloadCsv = () => {
    if (rows.length === 0) { toast.error("No submissions to export"); return; }
    const head = ["Submitted", "Full name", "Email", "Program", "Personas",
      ...FIELD_DEFS.map((f) => f.label),
      ...DIMENSIONS.map((d) => `${d.label} (%)`), "Signature"];
    const lines = [head.map(csvCell).join(",")];
    rows.forEach((a) => {
      const cells = [
        fmt(a.submitted_at), a.name, a.email, a.tier || "", personasOf(a).join("; "),
        ...FIELD_DEFS.map((f) => a.profile?.[f.name]),
        ...DIMENSIONS.map((d) => a.scores?.[d.key] ?? ""),
        a.profile?.ack_signature || "",
      ];
      lines.push(cells.map(csvCell).join(","));
    });
    const blob = new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `career-assessments-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="lg:col-span-2">
      <Panel title="Client assessments" count={rows.length} testId="admin-assessments-panel">
        <button data-testid="admin-assessments-csv" onClick={downloadCsv}
          className="inline-flex items-center gap-2 bg-[#041C3E] text-white px-5 py-3 text-sm font-semibold hover:bg-[#D94833] transition-colors">
          <Download size={15} /> Download all as CSV (opens in Google Sheets)
        </button>
        <div className="mt-5">
          {rows.length === 0 && <p className="text-sm text-[#9CA3AF]">No assessments submitted yet. These appear after a client pays and completes their intake.</p>}
          {rows.map((a) => (
            <div key={a.intake_token} data-testid={`admin-assessment-${a.intake_token}`} className="border-t border-[#E5E7EB] py-3">
              <div className="flex items-start justify-between gap-4">
                <button onClick={() => setOpenId(openId === a.intake_token ? null : a.intake_token)} className="text-left flex-1">
                  <p className="font-semibold text-sm flex items-center gap-1.5">
                    <ChevronDown size={14} className={`transition-transform ${openId === a.intake_token ? "rotate-180" : ""}`} />
                    {a.name}
                  </p>
                  <p className="text-xs text-[#9CA3AF] mt-0.5 ml-5">
                    {a.email} · {personasOf(a).join(", ") || "no persona"} · {fmt(a.submitted_at)}
                  </p>
                </button>
                <div className="flex items-center gap-2 shrink-0">
                  <button data-testid={`admin-assessment-resend-${a.intake_token}`} onClick={() => resend(a, false)} disabled={busyId === a.intake_token}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#041C3E] text-white px-3 py-1.5 rounded hover:bg-[#D94833] transition-colors disabled:opacity-50" title="Resend the report email">
                    <Send size={13} /> {busyId === a.intake_token ? "Sending" : "Resend"}
                  </button>
                  <a href={`mailto:${a.email}`} className="p-2 text-[#3A4A63] hover:text-[#041C3E]" title="Email"><ExternalLink size={15} /></a>
                  <button data-testid={`admin-assessment-delete-${a.intake_token}`} onClick={() => remove(a)} className="p-2 text-[#3A4A63] hover:text-[#D94833]" title="Delete"><Trash2 size={15} /></button>
                </div>
              </div>
              {openId === a.intake_token && (
                <div className="mt-3 ml-5 space-y-4" data-testid={`admin-assessment-detail-${a.intake_token}`}>
                  {a.scores && Object.keys(a.scores).length > 0 && (
                    <div className="space-y-1.5" data-testid={`admin-assessment-scorecard-${a.intake_token}`}>
                      <p className="text-[10px] uppercase tracking-[0.1em] text-[#D94833] font-bold">Aptitude scorecard</p>
                      {[...DIMENSIONS].sort((x, y) => (a.scores[y.key] ?? 0) - (a.scores[x.key] ?? 0)).map((d) => (
                        <div key={d.key} className="flex items-center gap-2">
                          <span className="text-xs w-24 text-[#3A4A63]">{d.label}</span>
                          <span className="flex-1 h-2 bg-[#F3F4F6] rounded overflow-hidden">
                            <span className="block h-2 bg-[#D94833]" style={{ width: `${a.scores[d.key] ?? 0}%` }} />
                          </span>
                          <span className="text-xs font-bold w-9 text-right text-[#050F24]">{a.scores[d.key] ?? 0}%</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {a.report && (a.report.thinking_style || a.report.ability_summary || (a.report.occupations || []).length > 0) ? (
                    <div className="space-y-3 border border-[#E5E7EB] rounded-lg p-4 bg-white" data-testid={`admin-assessment-report-${a.intake_token}`}>
                      <p className="text-[10px] uppercase tracking-[0.1em] text-[#D94833] font-bold flex items-center gap-1.5">
                        <Sparkles size={12} /> Career Discovery report (GPT-6 Astra)
                      </p>
                      {a.report.thinking_style && <ReportBlock label="How they think" text={a.report.thinking_style} />}
                      {a.report.ability_summary && <ReportBlock label="Abilities" text={a.report.ability_summary} />}
                      {a.report.deep_summary && <ReportBlock label="Deep summary" text={a.report.deep_summary} />}
                      {(a.report.occupations || []).length > 0 && (
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.1em] text-[#9CA3AF] font-semibold mb-1">Career directions</p>
                          <ul className="text-sm text-[#162030] space-y-1">
                            {a.report.occupations.map((o, i) => (
                              <li key={i}><strong>{o.title}</strong>{o.why ? ` — ${o.why}` : ""}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {(a.report.next_moves || []).length > 0 && (
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.1em] text-[#9CA3AF] font-semibold mb-1">Next moves (walk in prepared)</p>
                          <ol className="text-sm text-[#162030] space-y-1 list-decimal ml-4">
                            {a.report.next_moves.map((m, i) => (
                              <li key={i}><strong>{m.move}</strong>{m.detail ? ` — ${m.detail}` : ""}</li>
                            ))}
                          </ol>
                        </div>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {(a.report.colleges_india || []).length > 0 && <CollegeList label={`${a.report.recommendation_label || "Top 5"} in India`} items={a.report.colleges_india} />}
                        {(a.report.colleges_abroad || []).length > 0 && <CollegeList label={`${a.report.recommendation_label || "Top 5"} abroad`} items={a.report.colleges_abroad} />}
                      </div>
                      {a.report.focus_90_days && <ReportBlock label="Next 90 days" text={a.report.focus_90_days} />}
                      {a.report.owner_briefing && (
                        <div className="bg-[#F7F5F2] border-l-4 border-[#D94833] p-3">
                          <p className="text-[10px] uppercase tracking-[0.1em] text-[#D94833] font-bold">Session briefing</p>
                          <p className="text-sm text-[#162030] mt-1 leading-relaxed">{a.report.owner_briefing}</p>
                        </div>
                      )}
                      <button data-testid={`admin-assessment-regenerate-${a.intake_token}`} onClick={() => resend(a, true)} disabled={busyId === a.intake_token}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#3A4A63] hover:text-[#D94833] disabled:opacity-50">
                        <RefreshCw size={12} /> Regenerate and resend
                      </button>
                    </div>
                  ) : (
                    <div className="bg-[#F7F5F2] border-l-4 border-[#D94833] p-3" data-testid={`admin-assessment-summary-${a.intake_token}`}>
                      <p className="text-[10px] uppercase tracking-[0.1em] text-[#D94833] font-bold">Session briefing</p>
                      <p className="text-sm text-[#162030] mt-1 leading-relaxed">{a.ai_summary || "No AI report yet. Use Regenerate and resend to build a full Career Discovery report for this submission."}</p>
                      <button data-testid={`admin-assessment-regenerate-${a.intake_token}`} onClick={() => resend(a, true)} disabled={busyId === a.intake_token}
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#3A4A63] hover:text-[#D94833] disabled:opacity-50">
                        <Sparkles size={12} /> Generate full report and resend
                      </button>
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    {FIELD_DEFS.filter((f) => {
                      const v = a.profile?.[f.name];
                      return v && (Array.isArray(v) ? v.length : String(v).trim());
                    }).map((f) => {
                      const v = a.profile[f.name];
                      return (
                        <div key={f.name} className="border-b border-[#F3F4F6] pb-1.5">
                          <p className="text-[10px] uppercase tracking-[0.1em] text-[#9CA3AF]">{f.label}</p>
                          <p className="text-[#162030]">{Array.isArray(v) ? v.join(", ") : String(v)}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
};

const EnquiriesManager = ({ headers }) => {
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = () => axios.get(`${API}/admin/enquiries`, { headers }).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (e) => {
    if (!window.confirm(`Delete inquiry from ${e.name}?`)) return;
    try {
      await axios.delete(`${API}/admin/enquiries/${e.id}`, { headers });
      toast.success("Inquiry deleted");
      load();
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div className="lg:col-span-2">
      <Panel title="B2B inquiries" count={rows.length} testId="admin-enquiries-panel">
        {rows.length === 0 && <p className="text-sm text-[#9CA3AF]">No inquiries yet. Institutional inquiries from the pricing section appear here.</p>}
        {rows.map((e) => (
          <div key={e.id} data-testid={`admin-enquiry-${e.id}`} className="border-t border-[#E5E7EB] py-3">
            <div className="flex items-start justify-between gap-4">
              <button onClick={() => setOpenId(openId === e.id ? null : e.id)} className="text-left flex-1">
                <p className="font-semibold text-sm flex items-center gap-1.5">
                  <ChevronDown size={14} className={`transition-transform ${openId === e.id ? "rotate-180" : ""}`} />
                  {e.organisation || e.name}
                </p>
                <p className="text-xs text-[#9CA3AF] mt-0.5 ml-5">{e.name} · {e.email} · {fmt(e.created_at)}</p>
              </button>
              <div className="flex items-center gap-2 shrink-0">
                <a href={`mailto:${e.email}`} className="p-2 text-[#3A4A63] hover:text-[#041C3E]" title="Reply"><ExternalLink size={15} /></a>
                <button data-testid={`admin-enquiry-delete-${e.id}`} onClick={() => remove(e)} className="p-2 text-[#3A4A63] hover:text-[#D94833]" title="Delete"><Trash2 size={15} /></button>
              </div>
            </div>
            {openId === e.id && (
              <div className="mt-2 ml-5 text-sm text-[#162030]" data-testid={`admin-enquiry-detail-${e.id}`}>
                {e.phone && <p className="text-xs text-[#9CA3AF] mb-2">Phone: {e.phone}</p>}
                <p className="whitespace-pre-wrap leading-relaxed">{e.message}</p>
              </div>
            )}
          </div>
        ))}
      </Panel>
    </div>
  );
};

const BlogManager = ({ headers }) => {
  const [posts, setPosts] = useState([]);
  const [mode, setMode] = useState(null); // null | "new" | post object
  const [title, setTitle] = useState("");
  const [html, setHtml] = useState("");
  const [published, setPublished] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/admin/blog`, { headers }).then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startNew = () => { setMode("new"); setTitle(""); setHtml(""); setPublished(true); };
  const startEdit = (p) => { setMode(p); setTitle(p.title); setHtml(p.html); setPublished(p.published); };
  const cancel = () => setMode(null);

  const save = async () => {
    if (title.trim().length < 2) { toast.error("Add a title"); return; }
    setBusy(true);
    try {
      const body = { title, html, published };
      if (mode === "new") await axios.post(`${API}/admin/blog`, body, { headers });
      else await axios.put(`${API}/admin/blog/${mode.id}`, body, { headers });
      toast.success("Post saved");
      setMode(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save post");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/blog/${p.id}`, { headers });
      toast.success("Post deleted");
      load();
    } catch {
      toast.error("Could not delete post");
    }
  };

  return (
    <div className="lg:col-span-2">
      <Panel title="Blog" count={posts.length} testId="admin-blog-panel">
        {mode === null ? (
          <div>
            <button data-testid="admin-blog-new" onClick={startNew}
                    className="inline-flex items-center gap-2 bg-[#041C3E] text-white px-5 py-3 text-sm font-semibold hover:bg-[#D94833] transition-colors">
              <Plus size={15} /> New post
            </button>
            <div className="mt-5">
              {posts.length === 0 && <p className="text-sm text-[#9CA3AF]">No posts yet. Write your first one.</p>}
              {posts.map((p) => (
                <div key={p.id} data-testid={`admin-blog-row-${p.id}`}
                     className="border-t border-[#E5E7EB] py-3 flex items-start justify-between gap-4">
                  <div>
                    <p className="font-semibold text-sm">{p.title}</p>
                    <p className="text-xs text-[#9CA3AF] mt-0.5">
                      {p.published ? <span className="text-green-700 font-semibold">Published</span> : <span className="text-[#B45309] font-semibold">Draft</span>}
                      {" · "}/blog/{p.slug} · {fmt(p.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" title="View"
                       className="p-2 text-[#3A4A63] hover:text-[#041C3E]"><ExternalLink size={15} /></a>
                    <button data-testid={`admin-blog-edit-${p.id}`} onClick={() => startEdit(p)} title="Edit"
                            className="p-2 text-[#3A4A63] hover:text-[#041C3E]"><PenLine size={15} /></button>
                    <button data-testid={`admin-blog-delete-${p.id}`} onClick={() => remove(p)} title="Delete"
                            className="p-2 text-[#3A4A63] hover:text-[#D94833]"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <input data-testid="admin-blog-title" value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder="Post title"
                   className="w-full border border-[#E5E7EB] focus:border-[#041C3E] outline-none px-4 py-3 text-lg font-head font-bold" />
            <RichTextEditor value={html} onChange={setHtml} placeholder="Write your post..." />
            <label className="flex items-center gap-2 text-sm text-[#3A4A63]">
              <input data-testid="admin-blog-published" type="checkbox" checked={published}
                     onChange={(e) => setPublished(e.target.checked)} className="accent-[#041C3E]" />
              Published (visible on the site)
            </label>
            <div className="flex gap-3">
              <button data-testid="admin-blog-save" onClick={save} disabled={busy}
                      className="bg-[#041C3E] text-white px-6 py-3 text-sm font-semibold hover:bg-[#D94833] transition-colors disabled:opacity-40">
                {busy ? "Saving..." : "Save post"}
              </button>
              <button data-testid="admin-blog-cancel" onClick={cancel}
                      className="border border-[#E5E7EB] px-6 py-3 text-sm font-semibold hover:border-[#041C3E] transition-colors">
                Cancel
              </button>
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
};

const InviteManager = ({ headers }) => {
  const [rows, setRows] = useState([]);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/admin/intake-invites`, { headers }).then((r) => setRows(r.data.invites)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/intake-invites`, { label }, { headers });
      try { await navigator.clipboard.writeText(res.data.url); toast.success("Link created and copied to clipboard"); }
      catch { toast.success("Link created"); }
      setLabel("");
      load();
    } catch { toast.error("Could not create link"); }
    finally { setBusy(false); }
  };

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Copy failed. Select the link text manually."); }
  };

  const remove = async (t) => {
    if (!window.confirm("Delete this invite link? Anyone holding it will lose access.")) return;
    try { await axios.delete(`${API}/admin/intake-invites/${t}`, { headers }); toast.success("Link deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <div className="lg:col-span-2">
      <Panel title="Private intake links" count={rows.length} testId="admin-invites-panel">
        <p className="text-sm text-[#3A4A63] mb-3">
          Generate a private, single use link to the intake and Career Discovery Assessment for someone you are
          connecting with directly. It is not shown anywhere on the website, and it stops working once that person submits.
        </p>
        <div className="flex gap-2">
          <input data-testid="invite-label-input" value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder="Who is this for? (optional note)"
            className="flex-1 border border-[#E5E7EB] focus:border-[#041C3E] outline-none px-3 py-2 text-sm" />
          <button data-testid="invite-create-button" onClick={create} disabled={busy}
            className="inline-flex items-center gap-1 bg-[#041C3E] text-white px-4 py-2 text-sm font-semibold hover:bg-[#D94833] transition-colors disabled:opacity-50">
            <Plus size={15} /> {busy ? "Creating…" : "Create link"}
          </button>
        </div>
        {rows.length === 0 && <p className="text-sm text-[#9CA3AF] mt-4">No links yet. Create one to share directly.</p>}
        {rows.map((r) => (
          <div key={r.token} data-testid={`admin-invite-${r.token}`} className="border-t border-[#E5E7EB] py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm truncate">
                  {r.label || "Untitled invite"}
                  {r.used
                    ? <span className="ml-2 text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] border border-[#E5E7EB] px-1.5 py-0.5">Used</span>
                    : <span className="ml-2 text-[10px] uppercase tracking-[0.15em] text-[#0A7C4A] border border-[#0A7C4A] px-1.5 py-0.5">Active</span>}
                </p>
                <p className="text-xs text-[#9CA3AF] mt-1 break-all">{r.url}</p>
                <p className="text-xs text-[#9CA3AF] mt-0.5">
                  Created {fmt(r.created_at)}{r.used ? ` · used ${fmt(r.used_at)}${r.name ? ` by ${r.name}` : ""}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {!r.used && (
                  <button data-testid={`invite-copy-${r.token}`} onClick={() => copy(r.url)}
                    className="p-2 text-[#3A4A63] hover:text-[#041C3E]" title="Copy link"><Link2 size={15} /></button>
                )}
                <button data-testid={`invite-delete-${r.token}`} onClick={() => remove(r.token)}
                  className="p-2 text-[#3A4A63] hover:text-[#D94833]" title="Delete"><Trash2 size={15} /></button>
              </div>
            </div>
          </div>
        ))}
      </Panel>
    </div>
  );
};


const SiteContentManager = ({ headers }) => {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/site-content`).then((r) => {
    const d = r.data;
    setC({
      session_inr: d.prices.session.inr_amount,
      session_usd: d.prices.session.usd || "",
      membership_inr: d.prices.membership.inr_amount,
      membership_usd: d.prices.membership.usd || "",
      refund_banner: d.refund_banner || "",
      audiences: (d.audiences || []).map((a) => ({ title: a.title, points: (a.points || []).join("\n") })),
      audience_closing: d.audience_closing || "",
      inst_heading: d.institutional?.heading || "",
      inst_description: d.institutional?.description || "",
      inst_bullets: (d.institutional?.bullets || []).join("\n"),
      inst_note: d.institutional?.note || "",
    });
  }).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        prices: {
          session: { inr_amount: Number(c.session_inr) || 0, usd: c.session_usd },
          membership: { inr_amount: Number(c.membership_inr) || 0, usd: c.membership_usd },
        },
        refund_banner: c.refund_banner,
        audiences: c.audiences.map((a) => ({
          title: a.title,
          points: a.points.split("\n").map((p) => p.trim()).filter(Boolean),
        })),
        audience_closing: c.audience_closing,
        institutional: {
          heading: c.inst_heading,
          description: c.inst_description,
          bullets: c.inst_bullets.split("\n").map((b) => b.trim()).filter(Boolean),
          note: c.inst_note,
        },
      };
      await axios.put(`${API}/admin/site-content`, body, { headers });
      toast.success("Site content saved. Refresh the site to see changes.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save content.");
    } finally {
      setBusy(false);
    }
  };

  const inp = "w-full border border-[#E5E7EB] focus:border-[#041C3E] outline-none px-3 py-2 text-sm";
  const lbl = "text-xs font-semibold uppercase tracking-[0.12em] text-[#9CA3AF]";

  if (!c) return null;
  const setAud = (i, key, val) => setC({ ...c, audiences: c.audiences.map((a, j) => j === i ? { ...a, [key]: val } : a) });

  return (
    <div className="lg:col-span-2">
      <Panel title="Site content" testId="admin-site-content-panel">
        <p className="text-sm text-[#3A4A63] mb-5">Edit prices, the refund policy banner, the "Who this is for" groups, and the institutional section. Changes go live on the site after you save and the visitor refreshes.</p>

        <p className="font-head font-bold text-[#041C3E]">Prices</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          <div>
            <span className={lbl}>One Time Session — INR amount</span>
            <input data-testid="content-session-inr" type="number" className={inp} value={c.session_inr} onChange={(e) => setC({ ...c, session_inr: e.target.value })} />
            <span className={lbl}>One Time Session — USD label</span>
            <input data-testid="content-session-usd" className={inp} value={c.session_usd} onChange={(e) => setC({ ...c, session_usd: e.target.value })} />
          </div>
          <div>
            <span className={lbl}>Premium / Monthly — INR amount</span>
            <input data-testid="content-membership-inr" type="number" className={inp} value={c.membership_inr} onChange={(e) => setC({ ...c, membership_inr: e.target.value })} />
            <span className={lbl}>Premium / Monthly — USD label</span>
            <input data-testid="content-membership-usd" className={inp} value={c.membership_usd} onChange={(e) => setC({ ...c, membership_usd: e.target.value })} />
          </div>
        </div>

        <p className="font-head font-bold text-[#041C3E] mt-6">Refund / booking banner</p>
        <textarea data-testid="content-refund-banner" rows={4} className={`${inp} mt-2`} value={c.refund_banner} onChange={(e) => setC({ ...c, refund_banner: e.target.value })} />

        <p className="font-head font-bold text-[#041C3E] mt-6">Who this is for</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-2">
          {c.audiences.map((a, i) => (
            <div key={i} data-testid={`content-audience-${i}`}>
              <span className={lbl}>Group {i + 1} title</span>
              <input data-testid={`content-audience-title-${i}`} className={inp} value={a.title} onChange={(e) => setAud(i, "title", e.target.value)} />
              <span className={lbl}>Points (one per line)</span>
              <textarea data-testid={`content-audience-points-${i}`} rows={4} className={inp} value={a.points} onChange={(e) => setAud(i, "points", e.target.value)} />
            </div>
          ))}
        </div>
        <span className={`${lbl} block mt-4`}>Closing line</span>
        <input data-testid="content-audience-closing" className={inp} value={c.audience_closing} onChange={(e) => setC({ ...c, audience_closing: e.target.value })} />

        <p className="font-head font-bold text-[#041C3E] mt-6">Institutional section</p>
        <span className={`${lbl} block mt-2`}>Heading</span>
        <input data-testid="content-inst-heading" className={inp} value={c.inst_heading} onChange={(e) => setC({ ...c, inst_heading: e.target.value })} />
        <span className={`${lbl} block mt-3`}>Description</span>
        <textarea data-testid="content-inst-description" rows={3} className={inp} value={c.inst_description} onChange={(e) => setC({ ...c, inst_description: e.target.value })} />
        <span className={`${lbl} block mt-3`}>Bullets (one per line)</span>
        <textarea data-testid="content-inst-bullets" rows={4} className={inp} value={c.inst_bullets} onChange={(e) => setC({ ...c, inst_bullets: e.target.value })} />
        <span className={`${lbl} block mt-3`}>Note</span>
        <textarea data-testid="content-inst-note" rows={2} className={inp} value={c.inst_note} onChange={(e) => setC({ ...c, inst_note: e.target.value })} />

        <button data-testid="content-save" onClick={save} disabled={busy}
          className="mt-6 bg-[#041C3E] text-white px-8 py-3 font-semibold tracking-wide hover:bg-[#D94833] transition-colors disabled:opacity-50">
          {busy ? "Saving…" : "Save site content"}
        </button>
      </Panel>
    </div>
  );
};

const CouponsManager = ({ headers }) => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ code: "", tier: "membership", inr_amount: 6000, usd: "$149", email: "", max_uses: 1 });
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/admin/coupons`, { headers }).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/coupons`, {
        code: form.code.trim(), tier: form.tier,
        inr_amount: Number(form.inr_amount) || 0, usd: form.usd.trim(),
        email: form.email.trim(), max_uses: Number(form.max_uses) || 0,
      }, { headers });
      toast.success("Coupon created");
      setForm({ code: "", tier: "membership", inr_amount: 6000, usd: "$149", email: "", max_uses: 1 });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create coupon");
    } finally { setBusy(false); }
  };

  const patch = async (id, body) => {
    try { await axios.patch(`${API}/admin/coupons/${id}`, body, { headers }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    try { await axios.delete(`${API}/admin/coupons/${id}`, { headers }); toast.success("Coupon deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const inp = "w-full border border-[#E5E7EB] focus:border-[#041C3E] outline-none px-3 py-2 text-sm";
  const lbl = "text-xs font-semibold uppercase tracking-[0.12em] text-[#9CA3AF]";

  return (
    <div className="lg:col-span-2">
      <Panel title="Coupons" count={rows.length} testId="admin-coupons-panel">
        <p className="text-sm text-[#3A4A63] mb-4">Create a discount code for a plan. Bind it to one email address so only that person can redeem it. Leave the code blank to auto-generate a unique one.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <span className={lbl}>Code (blank = auto)</span>
            <input data-testid="coupon-code" className={inp} placeholder="RHELLEX40" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
          </div>
          <div>
            <span className={lbl}>Plan</span>
            <select data-testid="coupon-tier" className={inp} value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })}>
              <option value="session">One Time Session</option>
              <option value="membership">Premium / Monthly</option>
            </select>
          </div>
          <div>
            <span className={lbl}>Bind to email (optional)</span>
            <input data-testid="coupon-email" className={inp} placeholder="person@example.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <span className={lbl}>Discounted INR</span>
            <input data-testid="coupon-inr" type="number" className={inp} value={form.inr_amount} onChange={(e) => setForm({ ...form, inr_amount: e.target.value })} />
          </div>
          <div>
            <span className={lbl}>USD label</span>
            <input data-testid="coupon-usd" className={inp} value={form.usd} onChange={(e) => setForm({ ...form, usd: e.target.value })} />
          </div>
          <div>
            <span className={lbl}>Max uses (0 = unlimited)</span>
            <input data-testid="coupon-max-uses" type="number" className={inp} value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} />
          </div>
        </div>
        <button data-testid="coupon-create" onClick={create} disabled={busy}
          className="mt-4 bg-[#041C3E] text-white px-6 py-3 font-semibold tracking-wide hover:bg-[#D94833] transition-colors disabled:opacity-50">
          {busy ? "Creating…" : "Create coupon"}
        </button>

        <div className="mt-6">
          {rows.length === 0 && <p className="text-sm text-[#9CA3AF]">No coupons yet.</p>}
          {rows.map((c) => (
            <div key={c.id} data-testid={`coupon-row-${c.code}`} className="border-t border-[#E5E7EB] py-3 flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm">
                <p className="font-bold text-[#041C3E]">{c.code} <span className="font-normal text-[#9CA3AF]">· {c.tier === "membership" ? "Premium / Monthly" : "One Time Session"} · {c.inr}{c.usd ? ` / ${c.usd}` : ""}</span></p>
                <p className="text-xs text-[#9CA3AF]">
                  {c.email ? `Bound to ${c.email}` : "Any email"} · used {c.used_count}{c.max_uses ? ` / ${c.max_uses}` : " (unlimited)"} · {c.active ? "active" : "inactive"}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`coupon-toggle-${c.code}`} onClick={() => patch(c.id, { active: !c.active })}
                  className="text-xs font-semibold border border-[#041C3E] text-[#041C3E] px-3 py-1.5 hover:bg-[#041C3E] hover:text-white transition-colors">
                  {c.active ? "Deactivate" : "Activate"}
                </button>
                <button data-testid={`coupon-bind-${c.code}`} onClick={() => {
                  const email = window.prompt("Bind this coupon to which email? (leave blank to unbind)", c.email || "");
                  if (email !== null) patch(c.id, { email });
                }}
                  className="text-xs font-semibold border border-[#041C3E] text-[#041C3E] px-3 py-1.5 hover:bg-[#041C3E] hover:text-white transition-colors">
                  {c.email ? "Change email" : "Bind email"}
                </button>
                <button data-testid={`coupon-delete-${c.code}`} onClick={() => remove(c.id)} className="p-2 text-[#3A4A63] hover:text-[#D94833]" title="Delete"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
};




const CheckoutDataManager = ({ headers, token }) => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/admin/checkout-submissions`, { headers }).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/admin/checkout-submissions.csv`, { headers, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `donations_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download CSV");
    } finally { setBusy(false); }
  };

  const paidCount = rows.filter((r) => r.status === "paid").length;

  return (
    <div className="lg:col-span-2">
      <Panel title="Donations & checkout data" count={rows.length} testId="admin-checkout-data-panel">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <p className="text-sm text-[#3A4A63]">Every checkout submission (name, email, phone, package, amount). {paidCount} completed donation(s).</p>
          <button data-testid="checkout-csv-download" onClick={download} disabled={busy}
            className="inline-flex items-center gap-2 bg-[#041C3E] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#D94833] transition-colors disabled:opacity-50">
            <Download size={15} /> {busy ? "Preparing…" : "Download CSV (Excel)"}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-xs uppercase tracking-[0.1em] text-[#9CA3AF] border-b border-[#E5E7EB]">
                <th className="py-2 pr-4">Date</th><th className="py-2 pr-4">Name</th><th className="py-2 pr-4">Email</th>
                <th className="py-2 pr-4">Phone</th><th className="py-2 pr-4">Package</th><th className="py-2 pr-4">Amount</th>
                <th className="py-2 pr-4">Status</th>
              </tr>
            </thead>
            <tbody data-testid="checkout-data-rows">
              {rows.length === 0 && <tr><td colSpan={7} className="py-4 text-[#9CA3AF]">No submissions yet.</td></tr>}
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-[#F3F4F6]">
                  <td className="py-2 pr-4 whitespace-nowrap text-xs">{fmt(r.date)}</td>
                  <td className="py-2 pr-4">{r.name}</td>
                  <td className="py-2 pr-4 break-all">{r.email}</td>
                  <td className="py-2 pr-4 whitespace-nowrap">{r.phone || "—"}</td>
                  <td className="py-2 pr-4 whitespace-nowrap">{r.package}</td>
                  <td className="py-2 pr-4 whitespace-nowrap">{r.amount}</td>
                  <td className="py-2 pr-4"><span className={r.status === "paid" ? "text-green-700 font-semibold" : "text-[#9CA3AF]"}>{r.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
};



export default function Admin() {
  const [params] = useSearchParams();
  const [token, setToken] = useState(sessionStorage.getItem("admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => {
    if (!token) return;
    axios
      .get(`${API}/admin/overview`, { headers })
      .then((res) => { setData(res.data); setError(""); })
      .catch(() => { setData(null); sessionStorage.removeItem("admin_token"); setToken(""); });
  };

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      if (res.data.user.role !== "admin") { setError("This account is not an owner account."); return; }
      sessionStorage.setItem("admin_token", res.data.token);
      setToken(res.data.token);
      setError("");
    } catch (err) {
      setError(err.response?.data?.detail || "Incorrect email or password");
    } finally { setBusy(false); }
  };

  useEffect(load, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const g = params.get("google");
    if (g === "connected") toast.success("Google account connected");
    if (g === "error") toast.error("Google connection failed. Check credentials and redirect URI.");
  }, [params]);

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-[#F3F4F6]" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-sm bg-white border border-[#041C3E] p-8 space-y-4">
          <p className="font-head font-black text-2xl">The Next Move<span className="text-[#D94833]"> by Rhellex</span></p>
          <p className="text-xs uppercase tracking-[0.2em] text-[#9CA3AF]">Owner panel</p>
          <input data-testid="admin-email-input" type="email" placeholder="Email" value={email} autoComplete="username"
                 onChange={(e) => setEmail(e.target.value)}
                 className="w-full border border-[#E5E7EB] focus:border-[#041C3E] outline-none px-4 py-3 text-sm" />
          <input data-testid="admin-password-input" type="password" placeholder="Password" value={password} autoComplete="current-password"
                 onChange={(e) => setPassword(e.target.value)}
                 className="w-full border border-[#E5E7EB] focus:border-[#041C3E] outline-none px-4 py-3 text-sm" />
          {error && <p data-testid="admin-login-error" className="text-[#D94833] text-sm">{error}</p>}
          <button data-testid="admin-login-button" type="submit" disabled={busy} className="w-full bg-[#041C3E] text-white px-8 py-4 font-semibold hover:bg-[#D94833] transition-colors disabled:opacity-50">{busy ? "Signing in…" : "Sign in"}</button>
        </form>
      </div>
    );
  }

  const pending = data.testimonials.filter((t) => !t.approved);
  const published = data.testimonials.filter((t) => t.approved);

  return (
    <div className="min-h-screen bg-[#F3F4F6]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="font-head font-black">The Next Move<span className="text-[#D94833]"> by Rhellex</span></Link>
          <div className="flex items-center gap-4 text-xs uppercase tracking-[0.15em] text-[#9CA3AF]">
            {data.razorpay_demo && <span data-testid="admin-razorpay-demo">Razorpay: demo</span>}
            <button data-testid="admin-refresh" onClick={load} className="inline-flex items-center gap-1 hover:text-[#041C3E]"><RefreshCw size={12} /> Refresh</button>
            <button data-testid="admin-logout" onClick={() => { sessionStorage.removeItem("admin_token"); setToken(""); setData(null); }} className="hover:text-[#041C3E]">Sign out</button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="lg:col-span-2"><AnalyticsManager headers={headers} /></div>
        <GoogleCard google={data.google} headers={headers} reload={load} />
        <AvailabilityManager headers={headers} scope="default"
          title="Session & student call availability"
          intro={<>Open hours for paid sessions and <strong>Student Assessment</strong> calls, in <strong>your own timezone</strong>. Visitors see these converted to their local time; everything else is blocked automatically.</>} />
        <AvailabilityManager headers={headers} scope="business"
          title="Business meeting availability"
          intro={<>A <strong>separate</strong> set of open hours just for <strong>Business Meeting</strong> calls from your <code className="font-mono">/book</code> link. Leave blank to reuse the session hours. Both calendars still sync with Google and block each other's times.</>} />
        <div className="lg:col-span-2"><MeetingsManager headers={headers} /></div>
        <Panel title="Testimonials to review" count={pending.length} testId="admin-testimonials-panel">
          <AddTestimonial headers={headers} reload={load} />
          {pending.length === 0 && <p className="text-sm text-[#9CA3AF]">Nothing waiting for approval.</p>}
          {pending.map((t) => <TestimonialRow key={t.id} t={t} headers={headers} reload={load} />)}
          {published.length > 0 && (
            <details className="mt-4">
              <summary className="text-xs uppercase tracking-[0.15em] text-[#9CA3AF] cursor-pointer">Published ({published.length})</summary>
              {published.map((t) => <TestimonialRow key={t.id} t={t} headers={headers} reload={load} />)}
            </details>
          )}
        </Panel>
        <Panel title="Upcoming sessions" count={data.bookings.filter((b) => b.status !== "cancelled").length} testId="admin-bookings-panel">
          {data.bookings.filter((b) => b.status !== "cancelled").length === 0 && <p className="text-sm text-[#9CA3AF]">No sessions booked yet.</p>}
          {data.bookings.filter((b) => b.status !== "cancelled").map((b) => (
            <div key={b.id} className="border-t border-[#E5E7EB] py-3 text-sm flex justify-between gap-4">
              <div><p className="font-semibold">{b.name} <span className="text-[#9CA3AF] font-normal">· {b.tier_name}</span></p><p className="text-xs text-[#9CA3AF]">{b.email}</p></div>
              <div className="text-right"><p>{fmt(b.start)}</p><p className="text-xs uppercase tracking-[0.1em] text-[#9CA3AF]">{b.status}</p></div>
            </div>
          ))}
        </Panel>
        <Panel title="Recent purchases" count={data.payments.length} testId="admin-payments-panel">
          {data.payments.length === 0 && <p className="text-sm text-[#9CA3AF]">No purchases yet.</p>}
          {data.payments.map((p) => (
            <PaymentRow key={p.order_id} p={p} headers={headers} reload={load} />
          ))}
        </Panel>

        <Panel title="Chat leads" count={data.leads?.length || 0} testId="admin-leads-panel">
          {(!data.leads || data.leads.length === 0) && <p className="text-sm text-[#9CA3AF]">No chat leads captured yet.</p>}
          {(data.leads || []).map((l) => (
            <LeadRow key={l.id} lead={l} headers={headers} reload={load} />
          ))}
        </Panel>

        <BlogManager headers={headers} />
        <AssessmentsManager headers={headers} />
        <EnquiriesManager headers={headers} />
        <TaxCertManager headers={headers} />
        <AgreementsManager headers={headers} />
        <InviteManager headers={headers} />
        <SiteContentManager headers={headers} />
        <CouponsManager headers={headers} />
        <CheckoutDataManager headers={headers} token={token} />
      </main>
    </div>
  );
}
